import {
  AdType,
  CouponAdConf,
  PositionType,
  GetCouponListWithAdOpts,
  MergeCouponListItem,
  AdSet,
} from '@/types/we-coupons';
import { MerchantInfo } from '@tencent/wphk-components-wecoupon';
import { queryDpMessage } from '@tencent/wphk-components-dp-loader';
import { QryDpMsgRes } from '@tencent/wphk-components-dp-loader/src/types';
import { to, cacheFunc } from '@tencent/wphk-base-utils';
import { getSPInfoBySPId } from '@tencent/wphk-components-wecoupon/components/mch-coupon-list/api';
import { getShareInfoByIdList } from '@tencent/wphk-components-help-friend';
import { CustomException } from '@tencent/ppd-base-libs';
import { ShareCouponStatus } from '@tencent/wphk-act-base-cgi';

/**
 * 获取并格式化广告列表
 * @returns
 */
export async function getAdList() {
  // 获取抢券有 so 广告（投放中台）
  const adList = (await getWecouponCouponAd()) || [];
  // 拉取分享券extra配置（货源系统&后台wecoupon_qry_prize_info.cgi）
  const shareActConfig = await initShareActConfig(adList);
  return adList
    .filter((ele) => {
      if (ele.adType !== AdType.shareCouponAd) return true;
      if (ele.adCouponId && shareActConfig && shareActConfig[ele.adCouponId]) return true;
      return false;
    })
    .map((ele) => {
      const adInfo = {
        ...ele,
        type: PositionType.ad, // 添加广告位标识
      };
      // 分享券携带额外信息
      if (adInfo.adType === AdType.shareCouponAd && shareActConfig && ele.adCouponId) {
        adInfo.extra = shareActConfig[ele.adCouponId];
      }
      return adInfo;
    });
}
/**
 * 配置数据与后台数据整合
 * @param couponIdList
 * @returns
 */
export async function initShareActConfig(adList) {
  // 分享活动配置
  const shareActConfig: { [key: string]: any } = {};
  // 获取广告位 - 分享券配置券 id 列表
  const shareCouponAdList = adList.filter(ad => ad.adType === AdType.shareCouponAd);
  if (!shareCouponAdList.length) return;
  const shareCouponAdIdList = shareCouponAdList.map(ad => ad.adCouponId) || [];
  // 根据券 id 列表获取配置
  const [err, data] = await to(getShareInfoByIdList(shareCouponAdIdList));
  if (err) {
    new CustomException(err, 'initShareActConfigErr', '初始化分享券活动失败');
    return;
  }
  for (const item of data) {
    const { status } = item.couponInfo;
    // 过滤不展示的广告
    if ([ShareCouponStatus.notStart, ShareCouponStatus.end].includes(status)) continue;
    // @ts-ignore
    if (item.couponId) shareActConfig[item.couponId] = item;
  }
  return shareActConfig;
}

// 广告位标识（广告 id，投放计划 id，暂为商户 id）
const WECOUPON_AD_KEY = 'wecoupn_ad_key';
/** 投放页面id */
const DP_PAGE = '110004000024';
/** 投放位置id */
const DP_POSITION = '1100040000240001';
// // /** 投放页面id */
// const DP_PAGE = '110004553567';
// /** 投放位置id */
// const DP_POSITION = '1100045535670002';

/**
 * 设置广告 id（用于组件上报）
 */
export function setWecouponAdFlag(value: string) {
  sessionStorage.setItem(WECOUPON_AD_KEY, value);
}

/**
 * 清除广告 id（用于组件上报）
 */
export function removeWecouponAdFlag() {
  sessionStorage.removeItem(WECOUPON_AD_KEY);
}

/**
 * 过滤配置正确的广告
 *
 * 如果是抢券有So商户展示券广告位类型，需要有商户 id
 * 如果是自定义广告类型，需要有配置跳转链接
 * 如果是分享裂变券广告，需要有商户 id 和分享券 id
 * @param list
 */
function filterValidAd(list: CouponAdConf[]): CouponAdConf[] {
  return list.filter((ad: CouponAdConf) => (ad.adType === AdType.spAd && !!ad.adSpId)
      || (ad.adType === AdType.otherAd && (!!ad.adJumpUrl || !!ad.appid || !!ad.username))
      || (ad.adType === AdType.shareCouponAd && !!ad.adCouponId));
}

/**
 * 根据投放信息格式化广告列表
 */
function getWecouponCouponAdByDp(dpMsg: QryDpMsgRes): CouponAdConf[] {
  const list = dpMsg.dpList.map(item => ({
    dpId: item.dpId,
    ...item.contentBaseData,
    // @ts-ignore 将string转为bolean
    isShowAdIcon: item.contentBaseData.isShowAdIcon === '1',
  }));
  // 格式化过滤
  return filterValidAd(list as CouponAdConf[]);
}

/**
 * 获取抢券有 so 广告
 * @returns
 */
const cacheRequestConfig = cacheFunc(queryDpMessage);
export async function getWecouponCouponAd(): Promise<CouponAdConf[]> {
  const [err, dpMsg] = await to(cacheRequestConfig({ dpPage: DP_PAGE, dpPositions: [DP_POSITION] }));
  if (err || !dpMsg) {
    return [];
  }
  return getWecouponCouponAdByDp(dpMsg as QryDpMsgRes);
}

/**
 * 根据商户 id 获取商户信息
 * @param spId 商户 id
 * @returns
 */
export async function getMerchantInfo(spId: string): Promise<MerchantInfo> {
  return await getSPInfoBySPId(spId);
}

/**
 * 获取带有广告位的券列表
 * @param list 券列表
 * @param adList 广告位数组
 * @param classId 分类
 * @param isComplete 列表是否加载完成
 * @returns
 */
export function getCouponListWithAd({ list, adList, classId, isComplete }: GetCouponListWithAdOpts): MergeCouponListItem[] {
  // 不需要插入广告
  if (!list.length || !adList.length || !classId) return list;
  // 当前分类下广告
  const currentClassIdAdList = adList
    .filter((ad: CouponAdConf) => ad.adClassId === classId)
    .sort((a, b) => a.adOrder - b.adOrder);
  // 当前分类下没有广告
  if (!currentClassIdAdList.length) return list;
  // 需要将券列表的广告过滤掉，不然会重复插入广告
  const couponList = list.filter(coupon => coupon.type !== PositionType.ad);  // TODO: 有必要吗？

  // 先整理广告券，将相同adOrder的券集合到一块
  const currentClassifyAdList = classifyAdList(currentClassIdAdList);

  // 将广告位插入券列表中
  currentClassifyAdList.forEach((adset: AdSet) => {
    // 即将插入的位置
    const adOrder = Math.max(adset.adOrder - 1, 0);
    if (adOrder > list.length && isComplete) {
      // 如果广告位位置超过列表，则将广告位放置在最后
      couponList.push(adset);
    } else if (couponList[adOrder]) {
      // 当前位置不是需要插入的广告，则进行插入
      couponList.splice(adOrder, 0, adset);
    }
  });

  return couponList;
}

/**
 * 重新整理格式化广告券，将其整理成AdSet格式
 * @param adList 广告券列表
 */
function classifyAdList(adList: CouponAdConf[]): AdSet[] {
  // 自定义H5广告券 或 商户广告券
  const result: {
    [index: number]: AdSet;
  } = {};
  // 分享券
  const resultShare: AdSet[] = [];
  // 轮播最大slide
  const maxSlide = 5;

  adList.forEach((ad: CouponAdConf) => {
    switch (ad.adType) {
      case AdType.shareCouponAd:
        // 如果是分享券，单个一组
        resultShare.push({
          type: PositionType.ad,
          adOrder: ad.adOrder,
          data: [ad],
        });
        break;
      default:
        // 如果是 自定义H5广告券 或 商户广告券，单个或多个一组，以adOrder为维度 */
        if (!result[ad.adOrder]) {
          result[ad.adOrder] = {
            type: PositionType.ad,
            adOrder: ad.adOrder,
            data: [],
          };
        }
        // 同一个adOrder下，不超过5个轮播slide
        if (result[ad.adOrder].data.length < maxSlide) result[ad.adOrder].data.push(ad);
    }
  });

  return [...Object.values(result), ...resultShare];
}
